


import React from 'react';

// --- Placeholder Card Component ---
// Basic card with configurable width/height/content
const SimpleCard = ({ item, style = {} }) => {
  const cardBaseStyle = {
    flexShrink: 0, // Prevent cards from shrinking
    width: style.width || '150px', // Default width, allow override
    height: style.height || '200px', // Default height, allow override
    marginRight: '15px', // Spacing between cards
    borderRadius: '10px',
    overflow: 'hidden',
    backgroundColor: '#e0e0e0', // Placeholder background
    display: 'flex',
    flexDirection: 'column',
    position: 'relative', // For potential text overlays
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    ...style, // Allow additional inline styles
  };

  const imageStyle = {
     width: '100%',
     height: style.imageHeight || '70%', // Default image height percentage
     backgroundColor: '#ccc', // Placeholder bg
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
     color: '#888',
     fontSize: '12px',
     objectFit: 'cover' // If using real images later
  }

  const textStyle = {
      padding: '8px',
      fontSize: '14px',
      color: '#333'
  }

  return (
    <div style={cardBaseStyle}>
        {/* Placeholder for Image */}
        <div style={imageStyle}>
             {item.imageUrl ? (
                <img src={item.imageUrl} alt={item.title || `Item ${item.id}`} style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
             ) : (
                 <span>Image Placeholder</span>
             )}
        </div>
        {/* Placeholder for Title/Text */}
        <div style={textStyle}>
            {item.title || `Item ${item.id}`}
            {item.subtitle && <div style={{fontSize: '12px', color: '#777'}}>{item.subtitle}</div>}
        </div>
    </div>
  );
};


// --- Slider Section Component ---
const SliderSection = ({ title, subtitle, children }) => {
  const styles = {
    section: {
      marginBottom: '30px', // Space below each section
      paddingLeft: '15px', // Indent content slightly from left edge
    },
    title: {
      fontSize: '1.4rem',
      fontWeight: 'bold',
      marginBottom: '2px',
      color: '#333',
    },
    subtitle: {
      fontSize: '0.9rem',
      color: '#777',
      marginBottom: '15px',
    },
    sliderContainer: {
      display: 'flex',
      overflowX: 'auto',
      overflowY: 'hidden',
      paddingBottom: '10px',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none', // Firefox
      msOverflowStyle: 'none', // IE/Edge
    },
    hideScrollbar: { // Separate style for Webkit scrollbar hiding
      '&::-webkit-scrollbar': {
           display: 'none',
      }
    }
  };

  // Note: Applying ::-webkit-scrollbar via inline styles isn't directly possible.
  // You'd typically use a className and CSS file for that selector.
  // For this inline example, scrollbar might still be visible in Chrome/Safari.
  return (
    <section style={styles.section}>
      <h2 style={styles.title}>{title}</h2>
      {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
      <div style={styles.sliderContainer}> {/* Scrollbar might show in Webkit */}
        {children}
      </div>
    </section>
  );
};


// --- Sample Data ---
const inspiredItems = [
  { id: 'i1', title: 'Creator 1', imageUrl: 'https://via.placeholder.com/120x150/a2d2ff/000?text=Insp1' },
  { id: 'i2', title: 'Creator 2', imageUrl: 'https://via.placeholder.com/120x150/bde0fe/000?text=Insp2' },
  { id: 'i3', title: 'Creator 3', imageUrl: 'https://via.placeholder.com/120x150/ffafcc/000?text=Insp3' },
  { id: 'i4', title: 'Creator 4', imageUrl: 'https://via.placeholder.com/120x150/ffc8dd/000?text=Insp4' },
  { id: 'i5', title: 'Creator 5', imageUrl: 'https://via.placeholder.com/120x150/cdb4db/000?text=Insp5' },
];

const destinationItems = [
  { id: 'd1', title: 'City One', subtitle: 'Country', imageUrl: 'https://via.placeholder.com/250x150/a2d2ff/000?text=City+1' },
  { id: 'd2', title: 'City Two', subtitle: 'Country', imageUrl: 'https://via.placeholder.com/250x150/bde0fe/000?text=City+2' },
  { id: 'd3', title: 'City Three', subtitle: 'Country', imageUrl: 'https://via.placeholder.com/250x150/ffafcc/000?text=City+3' },
  { id: 'd4', title: 'City Four', subtitle: 'Country', imageUrl: 'https://via.placeholder.com/250x150/ffc8dd/000?text=City+4' },
];

const itineraryItems = [
  { id: 't1', title: "Puerto Rico's Prettiest Places", imageUrl: 'https://via.placeholder.com/300x200/bde0fe/000?text=Itinerary+1' },
  { id: 't2', title: 'Kyoto Cherry Blossoms', imageUrl: 'https://via.placeholder.com/300x200/ffafcc/000?text=Itinerary+2' },
  { id: 't3', title: 'Italian Riviera Road Trip', imageUrl: 'https://via.placeholder.com/300x200/cdb4db/000?text=Itinerary+3' },
];

const experienceItems = [
  { id: 'e1', title: 'Groveland, California', subtitle: '$289 night', imageUrl: 'https://via.placeholder.com/200x250/cdb4db/000?text=Exp+1' },
  { id: 'e2', title: 'Hiking Tour, Alps', subtitle: '$150 / person', imageUrl: 'https://via.placeholder.com/200x250/ffafcc/000?text=Exp+2' },
  { id: 'e3', title: 'Cooking Class, Rome', subtitle: '$90 / person', imageUrl: 'https://via.placeholder.com/200x250/a2d2ff/000?text=Exp+3' },
  { id: 'e4', title: 'Surfing Lesson, Bali', subtitle: '$50 / person', imageUrl: 'https://via.placeholder.com/200x250/bde0fe/000?text=Exp+4' },
];


// --- Main Page Component ---
function StellerPage() {
  const styles = {
      page: {
          paddingTop: '20px',
          maxWidth: '1000px', // Max width for the whole page content
          margin: '0 auto', // Center page content
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif', // Apply base font
      }
  }

  return (
    <div style={styles.page}>
      <SliderSection title="Get Inspired" subtitle="by your favorite creators">
        {inspiredItems.map(item => (
          // Pass specific styles for this card type
          <SimpleCard key={item.id} item={item} style={{ width: '120px', height: '150px', imageHeight: '100%' }} />
        ))}
      </SliderSection>

      <SliderSection title="Cool Destinations" subtitle="recommended by me">
        {destinationItems.map(item => (
          // Pass specific styles for this card type
          <SimpleCard key={item.id} item={item} style={{ width: '250px', height: '150px', imageHeight: '65%' }} />
        ))}
      </SliderSection>

      <SliderSection title="Featured Trip Itineraries" subtitle="recommended by me">
         {itineraryItems.map(item => (
           // Pass specific styles for this card type
          <SimpleCard key={item.id} item={item} style={{ width: '300px', height: '200px', imageHeight: '100%' }} />
        ))}
      </SliderSection>

      <SliderSection title="Book an Experience" subtitle="recommended by me">
         {experienceItems.map(item => (
           // Pass specific styles for this card type
          <SimpleCard key={item.id} item={item} style={{ width: '200px', height: '280px', imageHeight: '75%' }} />
        ))}
      </SliderSection>

      {/* Add more sections here */}

    </div>
  );
}

// Export the main page component
export default StellerPage;